import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { images, svg } from "../../config/images";
import { getContactDetails } from "../../api";

const Footer = () => {
  const [contact, setContact] = useState(null);
  const location = useLocation();

  const fetchContactDetails = async () => {
    try {
      const data = await getContactDetails();
      setContact(data || null);
    } catch (_err) {
      // Fail silently and fall back to static defaults
      setContact(null);
    }
  };

  useEffect(() => {
    fetchContactDetails();
  }, [location.pathname]);

  const address = contact?.address || "";
  const email = contact?.email || "";
  const mainPhone = contact?.whatsapp || "";
  const allCourseNo1 = contact?.all_course_no1 || "";
  const allCourseNo2 = contact?.all_course_no2 || "";
  const tradingNo = contact?.trading_no || "";
  const facebookHref = contact?.facebook_link || "";
  const twitterHref = contact?.twitter_link || "";
  const instagramHref = contact?.instagram || "";

  return (
    <footer className="relative bg-gradient-to-b from-gray-800 to-gray-900 text-white mt-auto">
      {/* Main Footer Content Area */}
      <div className="max-w-7xl 4xl:max-w-10xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mb-12">
          {/* Column 1: Market Mavericks - Address & Social Media */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <img
                src={images.logo}
                alt="Market Mavericks Logo"
                className="h-12 w-auto"
              />
              <h3 className="text-2xl font-bold text-primary-400">
                Market Mavericks
              </h3>
            </div>
            {address && (
              <p className="text-gray-300 text-sm mb-6 leading-relaxed">
                {address}
              </p>
            )}
            <div className="flex gap-3">
              {facebookHref && (
                <a
                  href={facebookHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-11 h-11 rounded-full bg-white/10 hover:bg-[#1877F2] border-2 border-white/20 hover:border-[#1877F2] flex items-center justify-center transition-all duration-300 transform hover:scale-110"
                >
                  <img
                    src={svg.facebook}
                    alt="Facebook"
                    className="w-5 h-5 text-white"
                  />
                </a>
              )}
              {twitterHref && (
                <a
                  href={twitterHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-11 h-11 rounded-full bg-white/10 hover:bg-black border-2 border-white/20 hover:border-black flex items-center justify-center transition-all duration-300 transform hover:scale-110"
                >
                  <img
                    src={images.twitter}
                    alt="Twitter"
                    className="w-5 h-5 text-white"
                  />
                </a>
              )}
              {instagramHref && (
                <a
                  href={instagramHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-11 h-11 rounded-full bg-white/10 hover:bg-gradient-to-tr hover:from-[#f09433] hover:via-[#dc2743] hover:to-[#bc1888] border-2 border-white/20 hover:border-transparent flex items-center justify-center transition-all duration-300 transform hover:scale-110"
                >
                  <img
                    src={svg.instagram}
                    alt="Instagram"
                    className="w-5 h-5 text-white"
                  />
                </a>
              )}
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h3 className="text-lg font-bold text-primary-400 mb-5">
              Quick Links
            </h3>
            <ul className="space-y-3">
              {/* <li>
                <Link
                  to="/brokers"
                  className="text-gray-300 hover:text-primary-400 transition duration-200 flex items-center gap-2 group"
                >
                  <span className="w-1.5 h-1.5 bg-primary-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  <span>All Brokers</span>
                </Link>
              </li> */}
              <li>
                <Link
                  to="/quiz"
                  className="text-gray-300 hover:text-primary-400 transition duration-200 flex items-center gap-2 group"
                >
                  <span className="w-1.5 h-1.5 bg-primary-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  <span>Quiz</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/career"
                  className="text-gray-300 hover:text-primary-400 transition duration-200 flex items-center gap-2 group"
                >
                  <span className="w-1.5 h-1.5 bg-primary-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  <span>Careers</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className="text-gray-300 hover:text-primary-400 transition duration-200 flex items-center gap-2 group"
                >
                  <span className="w-1.5 h-1.5 bg-primary-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  <span>About Us</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-gray-300 hover:text-primary-400 transition duration-200 flex items-center gap-2 group"
                >
                  <span className="w-1.5 h-1.5 bg-primary-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  <span>Contact Us</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Contact Info */}
          <div>
            <h3 className="text-lg font-bold text-primary-400 mb-5">Contact</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <img
                  src={svg.email}
                  alt="Email"
                  className="w-5 h-5 text-primary-400 mt-1 flex-shrink-0"
                />
                {email && (
                  <a
                    href={`mailto:${email}`}
                    className="text-gray-300 text-sm hover:text-primary-400 transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {email}
                  </a>
                )}
              </div>
              <div className="flex items-start gap-3">
                <img
                  src={svg.phone}
                  alt="Phone"
                  className="w-5 h-5 text-primary-400 mt-1 flex-shrink-0"
                />
                {mainPhone && (
                  <a
                    href={`tel:${mainPhone}`}
                    className="text-gray-300 text-sm hover:text-primary-400 transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {mainPhone}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Enquiry Contact Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
          <div className="bg-gradient-to-br from-primary-500/20 to-primary-600/20 backdrop-blur-sm rounded-xl p-5 border border-primary-500/30 hover:border-primary-400 transition-all duration-300">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                <img
                  src={svg.phone}
                  alt="Phone"
                  className="w-6 h-6 text-white"
                />
              </div>
              <div>
                <p className="text-white font-semibold text-sm mb-1">
                  All Courses
                </p>
                {(allCourseNo1 || allCourseNo2) && (
                  <p className="text-gray-300 text-xs">
                    {allCourseNo1}
                    {allCourseNo2 ? `, ${allCourseNo2}` : ""}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-secondary-500/20 to-secondary-600/20 backdrop-blur-sm rounded-xl p-5 border border-secondary-500/30 hover:border-secondary-400 transition-all duration-300">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-secondary-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                <img
                  src={svg.phone}
                  alt="Phone"
                  className="w-6 h-6 text-white"
                />
              </div>
              <div>
                <p className="text-white font-semibold text-sm mb-1">Trading</p>
                {tradingNo && (
                  <p className="text-gray-300 text-xs">{tradingNo}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Policy and Disclaimer Section */}
        <div className="border-t border-gray-700 pt-6 pb-4">
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <Link
              to="/privacy"
              className="text-gray-400 hover:text-primary-400 transition duration-200"
            >
              Privacy Policy
            </Link>
            <span className="text-gray-600">•</span>
            <Link
              to="/refund"
              className="text-gray-400 hover:text-primary-400 transition duration-200"
            >
              Non Refund Policy
            </Link>
            <span className="text-gray-600">•</span>
            <Link
              to="/terms"
              className="text-gray-400 hover:text-primary-400 transition duration-200"
            >
              Terms & Condition
            </Link>
            <span className="text-gray-600">•</span>
            <Link
              to="/disclaimer"
              className="text-gray-400 hover:text-primary-400 transition duration-200"
            >
              Educational Disclaimer
            </Link>
          </div>
        </div>

        {/* Copyright Information */}
        <div className="border-t border-gray-700 pt-6">
          <p className="text-gray-400 text-sm text-center">
            Copyright © {new Date().getFullYear()}. Market Mavericks. All rights
            reserved.
          </p>
        </div>
      </div>

      {/* Wavy Background */}
      <div className="relative w-full h-24 overflow-hidden">
        <svg
          className="absolute bottom-0 w-full h-full"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
        >
          <path
            d="M0,60 C300,20 600,100 900,50 C1050,30 1125,40 1200,50 L1200,120 L0,120 Z"
            fill="url(#wave-gradient-footer)"
            opacity="0.4"
          />
          <path
            d="M0,80 C300,40 600,120 900,70 C1050,50 1125,60 1200,70 L1200,120 L0,120 Z"
            fill="url(#wave-gradient-footer)"
            opacity="0.3"
          />
          <defs>
            <linearGradient
              id="wave-gradient-footer"
              x1="0%"
              y1="0%"
              x2="0%"
              y2="100%"
            >
              <stop offset="0%" stopColor="#38915c" />
              <stop offset="100%" stopColor="#22c55e" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Floating Action Buttons */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3">
        {/* WhatsApp Button */}
        <a
          href="https://chat.whatsapp.com/D5ZQJZxkDk9H4jgcWNMWAG"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center gap-2 font-medium"
        >
          <img src={svg.whatsapp} alt="WhatsApp" className="w-5 h-5" />
          <span>Market Mavericks</span>
        </a>
      </div>
    </footer>
  );
};

export default Footer;
